// Administrative correction (D8, CR-027): his rule lets the administrator correct workflow metadata but not the
// substantive score. The field is one of the closed metadata fields (timestamp, source, evidence, withdrawn from
// evidence); every correction carries a reason and an evidence reference, and is appended, never an edit of the card.
import { useState } from "react";
import {
  ASSESSMENT_STATUS, Button, Dialog, DIALOG_LABELS, Field, HISTORY_LABELS, Input, Select, SLIDE_LABELS, Stack, TextArea,
} from "@/mc";
import { scorecardCorrectMetadata } from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import type { RoomPanelProps } from "@/views/b7/room/types";
import { DECIDE_LABELS, DECIDE_RULES, reasonOk, ROOM_KEYS, type CorrectionField } from "./labels";

/** The fields with his words. recorded_in_error has none yet (a debt), so it is not offered. */
export const CORRECTION_OPTIONS: readonly { value: CorrectionField; label: string }[] = [
  { value: "effective_at", label: HISTORY_LABELS.timestamp },
  { value: "source", label: HISTORY_LABELS.source },
  { value: "source_file_id", label: HISTORY_LABELS.evidence },
  { value: "withdrawn_from_evidence", label: ASSESSMENT_STATUS.withdrawn },
];

export function CorrectionDialog({ view, onChanged, scorecardId }: RoomPanelProps & { scorecardId?: string | undefined }) {
  const write = useWrite(scorecardCorrectMetadata, ROOM_KEYS);
  const [open, setOpen] = useState(false);
  const [field, setField] = useState<CorrectionField | "">("");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  if (!scorecardId) return null;
  const complete = field !== "" && value.trim() !== "" && reasonOk(reason) && evidence.trim() !== "";
  const submit = async () => {
    if (!complete) return;
    setRefused(null);
    try {
      await write.mutateAsync({
        requisition_id: view.requisition_id,
        scorecard_id: scorecardId,
        field,
        value: value.trim(),
        reason: reason.trim(),
        evidence_ref: evidence.trim(),
      });
      setOpen(false);
      setField("");
      setValue("");
      setReason("");
      setEvidence("");
      onChanged();
    } catch (e) {
      setRefused(errorText(e));
    }
  };
  return (
    <>
      <Button tone="ghost"  can={view.can.correct_metadata !== false} onClick={() => setOpen(true)}>
        {DECIDE_LABELS.administrativeCorrection}
      </Button>
      <Dialog
        title={DECIDE_LABELS.administrativeCorrection}
        open={open}
        onClose={() => setOpen(false)}
        dismissLabel={DIALOG_LABELS.cancel}
        hint={DECIDE_RULES.correctionRule}
        refused={refused ? { reason: refused } : undefined}
        primary={{
          label: DECIDE_LABELS.administrativeCorrection,
          onClick: () => void submit(),
          blocked: complete ? undefined : { reason: DECIDE_RULES.correctionRule },
          busy: write.isPending,
          hs: "room.correct.submit",
        }}
      >
        <Stack gap={8}>
          <Field label={DECIDE_LABELS.administrativeCorrection} required>
            <Select options={CORRECTION_OPTIONS} value={field} onChange={(v) => setField(v as CorrectionField)} required />
          </Field>
          <Field label={field ? CORRECTION_OPTIONS.find((o) => o.value === field)?.label ?? HISTORY_LABELS.source : HISTORY_LABELS.source} required>
            <Input value={value} onChange={(e) => setValue(e.target.value)} required />
          </Field>
          <TextArea label={SLIDE_LABELS.reason} required value={reason} onChange={(e) => setReason(e.target.value)} />
          <Field label={HISTORY_LABELS.evidence} required>
            <Input value={evidence} onChange={(e) => setEvidence(e.target.value)} required />
          </Field>
        </Stack>
      </Dialog>
    </>
  );
}
